import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';

/**
 * A function that generates an unique id
 */
type IDGenerator = () => string;

/**
 * An helper to generate an unique id stored in the local storage.
 * Such ID stays the same until when the local storage is not erased.
 *
 * @param keySuffix A suffix used to generate an unique key to store the id. The name of the product may be a good choice
 * @returns An `IdGenerator` that can be passed to pico
 */
export const localStorageIdGenerator = (keySuffix: string): IDGenerator => {
  if (typeof window === 'undefined') {
    throw new Error('This helper can only be used in web-based environments');
  }

  const itemKeyName = `LOCAL_STORAGE_ID_${keySuffix}`;

  return () => {
    const localStorageId = window.localStorage.getItem(itemKeyName);

    if (localStorageId == null) {
      const newLocalStorageId = uuid();
      window.localStorage.setItem(itemKeyName, newLocalStorageId);
      return newLocalStorageId;
    }

    return localStorageId;
  };
};

/**
 * An helper to generate an unique id stored in the session storage.
 * Such ID stays the same until when the session storage is not erased.
 *
 * @param keySuffix A suffix used to generate an unique key to store the id. The name of the product may be a good choice
 * @param uuidGenerator A function that generates an unique uuid4 string
 * @returns An `IdGenerator` that can be passed to pico
 */
export const sessionStorageIdGenerator = (keySuffix: string): IDGenerator => {
  if (typeof window === 'undefined') {
    throw new Error('This helper can only be used in web-based environments');
  }

  const itemKeyName = `SESSION_STORAGE_ID_${keySuffix}`;

  return () => {
    const sessionStorageId = window.sessionStorage.getItem(itemKeyName);

    if (sessionStorageId == null) {
      const newsessionStorageId = uuid();
      window.sessionStorage.setItem(itemKeyName, newsessionStorageId);
      return newsessionStorageId;
    }

    return sessionStorageId;
  };
};

/**
 * A helper to generate a unique id stored in a cookie.
 * Such ID stays the same until the cookie expires or is manually deleted.
 *
 * @param keySuffix A suffix used to generate an unique key to store the id. The name of the product may be a good choice
 * @param uuidGenerator A function that generates an unique uuid4 string
 * @param cookieDomain Specify the cookie domain attribute
 * @returns An `IdGenerator` that can be passed to pico
 */
export const cookieIdGenerator = (keySuffix: string, cookieDomain: string): IDGenerator => {
  if (typeof window === 'undefined') {
    throw new Error('This helper can only be used in web-based environments');
  }

  const itemKeyName = `COOKIE_ID_${keySuffix}`;

  return () => {
    const cookieId = Cookies.get(itemKeyName) ?? uuid();

    Cookies.set(itemKeyName, cookieId, { expires: 365, domain: cookieDomain });

    return cookieId;
  };
};
